import {
  type ElementRef,
  type MouseEventHandler,
  type ReactNode,
  useCallback,
  useContext,
} from 'react'
import {
  Link as ReactRouterLink,
  type LinkProps as ReactRouterLinkProps,
  useHref,
} from 'react-router'
import { ConfigContext } from '#src/contexts/config/ConfigContext.tsx'
import { useAddFlowNodeFromUrl } from '#src/hooks/useAddFlowNodeFromUrl.ts'
import { useIsInDesk } from '#src/hooks/useIsInDesk.ts'
import { forwardRef } from '#src/utils/forwardRef.ts'
import { shouldReloadApp } from '#src/utils/shouldReloadApp.ts'

export interface LinkProps extends ReactRouterLinkProps {
  to: ReactRouterLinkProps['to']
  preventScrollReset?: ReactRouterLinkProps['preventScrollReset']
  relative?: ReactRouterLinkProps['relative']
  reloadDocument?: ReactRouterLinkProps['reloadDocument']
  replace?: ReactRouterLinkProps['replace']
  state?: ReactRouterLinkProps['state']
  children: ReactNode
  disableFlowNodeNavigation?: boolean
}

export const Link = forwardRef<ElementRef<typeof ReactRouterLink>, LinkProps>(
  ({ to, onClick, target, disableFlowNodeNavigation, ...delegated }, ref) => {
    const addFlowNode = useAddFlowNodeFromUrl(to)
    const isInDesk = useIsInDesk()
    // A host outside Portal (portalOrigin) opens Portal routes on Portal,
    // wherever the link renders, portaled drawers and toasts included.
    const portalOrigin = useContext(ConfigContext)?.portalOrigin
    const path = useHref(to, { relative: delegated.relative })
    // Scheme and protocol-relative URLs already name their own host; empty
    // and hash-only placeholders (unresolved mentions) stay on this page.
    const onPortal =
      !!portalOrigin &&
      !(typeof to === 'string' && /^($|#|[a-z][a-z\d+.-]*:|\/\/)/i.test(to))
    const handleClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(
      (e) => {
        if (
          addFlowNode &&
          !e.metaKey && // We want cmd+click to open in new tab
          !disableFlowNodeNavigation
        ) {
          e.preventDefault()
          addFlowNode()
        }
        onClick?.(e)
      },
      [addFlowNode, onClick, disableFlowNodeNavigation]
    )
    return (
      <ReactRouterLink
        to={onPortal ? new URL(path, portalOrigin).href : to}
        ref={ref}
        reloadDocument={shouldReloadApp()}
        target={target ?? (onPortal || isInDesk ? '_blank' : undefined)}
        onClick={handleClick}
        {...delegated}
      />
    )
  }
)
